import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import Sidebar from '../../components/layout/Sidebar';
import Header from '../../components/layout/Header';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Slider } from '../../components/ui/slider';
import { 
  Rocket,
  TrendingUp,
  Target,
  Users,
  BarChart3,
  Calendar,
  FileText,
  CheckCircle,
  ArrowRight,
  Zap,
  Globe,
  MousePointer,
  ShoppingCart,
  Award,
  Clock,
  Send,
  Building2,
  Briefcase,
  Phone,
  Mail,
  Instagram,
  Facebook,
  Linkedin,
  Twitter,
  Youtube,
  MessageCircle,
  Radio,
  Megaphone,
  Store,
  GripVertical,
  ChevronRight,
  ChevronLeft,
  Loader2,
  CalendarDays,
  User,
  CheckCircle2,
  XCircle,
  AlertCircle,
  PartyPopper
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '../../components/ui/select';
import { cn } from '../../lib/utils';
import api from '../../lib/axios';
import { toast } from 'sonner';

const CopiloteDiscoverPage = () => {
  const { user } = useAuth();
  const { theme } = useTheme();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(true);
  
  // Request status tracking
  const [existingRequest, setExistingRequest] = useState(null);
  const [selectedRdvSlot, setSelectedRdvSlot] = useState(null);
  const [bookingRdv, setBookingRdv] = useState(false);
  
  // Form state
  const [formData, setFormData] = useState({
    // Step 1: Company info
    sector: '',
    avgBasket: '',
    clientType: '', // B2C, B2B, both, BtoBtoC
    hasClientFile: null,
    
    // Step 2: Current communication
    currentChannels: [],
    whoManagesCommunication: '',
    hasCommercialTeam: null,
    availableTime: null, // 1h/week
    
    // Step 3: Budget and objectives
    budget: 1500,
    objectives: [],
    objectivesPriority: {},
    expectations: ''
  });

  // Fetch existing request on mount
  useEffect(() => {
    fetchExistingRequest();
  }, []);

  const fetchExistingRequest = async () => {
    try {
      const res = await api.get('/copilote/my-request');
      if (res.data) {
        setExistingRequest(res.data);
      }
    } catch (error) {
      // No existing request
    } finally {
      setLoading(false);
    }
  };

  const handleBookRdv = async () => {
    if (!selectedRdvSlot || !existingRequest) return;
    
    setBookingRdv(true);
    try {
      const res = await api.post(`/copilote/requests/${existingRequest.id}/book-rdv`, {
        selected_slot: selectedRdvSlot
      });
      setExistingRequest(res.data);
      toast.success('RDV confirmé ! Vous recevrez un email de confirmation.');
    } catch (error) {
      toast.error('Erreur lors de la réservation du RDV');
    } finally {
      setBookingRdv(false);
    }
  };

  const steps = [
    { title: 'Votre entreprise', icon: Building2 },
    { title: 'Communication actuelle', icon: Megaphone },
    { title: 'Budget & Objectifs', icon: Target }
  ];

  const sectors = [
    'E-commerce / Retail',
    'Services aux entreprises (B2B)',
    'Services aux particuliers (B2C)',
    'Industrie / Manufacturing',
    'Santé / Bien-être',
    'Immobilier',
    'Finance / Assurance',
    'Restauration / Hôtellerie',
    'Formation / Éducation',
    'Technologie / SaaS',
    'Artisanat / Métiers manuels',
    'Transport / Logistique',
    'Autre'
  ];

  const communicationChannels = [
    { id: 'website', label: 'Site web', icon: Globe },
    { id: 'seo', label: 'SEO / Référencement naturel', icon: Target },
    { id: 'google_ads', label: 'Google Ads', icon: MousePointer },
    { id: 'facebook', label: 'Facebook / Meta Ads', icon: Facebook },
    { id: 'instagram', label: 'Instagram', icon: Instagram },
    { id: 'linkedin', label: 'LinkedIn', icon: Linkedin },
    { id: 'twitter', label: 'Twitter / X', icon: Twitter },
    { id: 'youtube', label: 'YouTube', icon: Youtube },
    { id: 'tiktok', label: 'TikTok', icon: Radio },
    { id: 'email', label: 'Email marketing', icon: Mail },
    { id: 'sms', label: 'SMS marketing', icon: MessageCircle },
    { id: 'print', label: 'Print / Flyers', icon: FileText },
    { id: 'radio', label: 'Radio', icon: Radio },
    { id: 'tv', label: 'TV', icon: Radio },
    { id: 'affichage', label: 'Affichage / OOH', icon: Store },
    { id: 'salons', label: 'Salons / Événements', icon: Users },
    { id: 'partenariats', label: 'Partenariats / Influence', icon: Users },
    { id: 'bouche_oreille', label: 'Bouche à oreille', icon: MessageCircle },
    { id: 'prospection', label: 'Prospection téléphonique', icon: Phone },
    { id: 'aucun', label: 'Aucun canal actif', icon: Clock }
  ];

  const objectivesList = [
    { id: 'traffic', label: 'Plus de trafic sur le site', icon: Globe },
    { id: 'clients', label: 'Plus de clients / leads', icon: Users },
    { id: 'basket', label: 'Augmenter le panier moyen', icon: ShoppingCart },
    { id: 'notoriety', label: 'Augmenter la notoriété', icon: Megaphone },
    { id: 'retention', label: 'Fidéliser les clients existants', icon: Award },
    { id: 'conversion', label: 'Améliorer le taux de conversion', icon: Target },
    { id: 'roi', label: 'Mesurer le ROI des actions', icon: TrendingUp },
    { id: 'automation', label: 'Automatiser la communication', icon: Zap }
  ];

  const whoManagesOptions = [
    'Je gère moi-même',
    'Un employé en interne',
    'Une agence de communication',
    'Un freelance',
    'Personne actuellement',
    'Plusieurs personnes / équipe dédiée'
  ];

  const toggleChannel = (channelId) => {
    setFormData(prev => ({
      ...prev,
      currentChannels: prev.currentChannels.includes(channelId)
        ? prev.currentChannels.filter(c => c !== channelId)
        : [...prev.currentChannels, channelId]
    }));
  };

  const toggleObjective = (objId) => {
    setFormData(prev => {
      const newObjectives = prev.objectives.includes(objId)
        ? prev.objectives.filter(o => o !== objId)
        : [...prev.objectives, objId];
      
      // Update priorities
      const newPriorities = { ...prev.objectivesPriority };
      if (!newObjectives.includes(objId)) {
        delete newPriorities[objId];
      } else if (!newPriorities[objId]) {
        newPriorities[objId] = newObjectives.length;
      }
      
      return {
        ...prev,
        objectives: newObjectives,
        objectivesPriority: newPriorities
      };
    });
  };

  const updatePriority = (objId, priority) => {
    setFormData(prev => ({
      ...prev,
      objectivesPriority: {
        ...prev.objectivesPriority,
        [objId]: priority
      }
    }));
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const res = await api.post('/copilote/request', {
        ...formData,
        client_id: user.id
      });
      setExistingRequest(res.data);
      toast.success('Demande envoyée ! L\'agence vous contactera rapidement.');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'envoi');
    } finally {
      setSubmitting(false);
    }
  };

  const canProceed = () => {
    switch (currentStep) {
      case 0:
        return formData.sector && formData.clientType && formData.hasClientFile !== null;
      case 1:
        return formData.currentChannels.length > 0 && formData.whoManagesCommunication && formData.availableTime !== null;
      case 2:
        return formData.objectives.length > 0 && formData.expectations.trim().length > 0;
      default:
        return false;
    }
  };

  const benefits = [
    { icon: TrendingUp, title: 'ROI Mesuré', desc: 'Suivez le retour sur investissement' },
    { icon: Target, title: 'Objectifs Chiffrés', desc: 'KPIs clairs et mesurables' },
    { icon: Globe, title: 'Multicanal', desc: 'Campagnes SEO, Ads, Social...' },
    { icon: BarChart3, title: 'Rapports Détaillés', desc: 'Projeté vs réalisé' },
    { icon: Calendar, title: 'Planning', desc: 'Vision claire des actions' },
    { icon: Users, title: 'Account Manager', desc: 'Interlocuteur dédié' }
  ];

  // Format date for display
  const formatDate = (isoString) => {
    const date = new Date(isoString);
    return date.toLocaleDateString('fr-FR', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Get status badge style
  const getStatusBadge = (status) => {
    switch (status) {
      case 'pending':
        return { label: 'En attente', color: 'bg-yellow-100 text-yellow-700 border-yellow-200', icon: Clock };
      case 'reviewing':
        return { label: 'En cours d\'analyse', color: 'bg-blue-100 text-blue-700 border-blue-200', icon: AlertCircle };
      case 'approved':
        return { label: 'Approuvée', color: 'bg-green-100 text-green-700 border-green-200', icon: CheckCircle2 };
      case 'rdv_booked':
        return { label: 'RDV confirmé', color: 'bg-emerald-100 text-emerald-700 border-emerald-200', icon: CalendarDays };
      case 'rejected':
        return { label: 'Non éligible', color: 'bg-red-100 text-red-700 border-red-200', icon: XCircle };
      default:
        return { label: status, color: 'bg-gray-100 text-gray-700', icon: AlertCircle };
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#f8fafc]">
        <Sidebar collapsed={collapsed} setCollapsed={setCollapsed} mobileOpen={mobileOpen} setMobileOpen={setMobileOpen} />
        <Header collapsed={collapsed} setMobileOpen={setMobileOpen} />
        <main className={cn("transition-all duration-300 p-6 md:p-8 pt-24 flex items-center justify-center", collapsed ? "md:ml-20" : "md:ml-64")}>
          <Loader2 className="w-8 h-8 animate-spin text-[#00d4aa]" />
        </main>
      </div>
    );
  }

  // Show existing request status
  if (existingRequest) {
    const statusInfo = getStatusBadge(existingRequest.status);
    const StatusIcon = statusInfo.icon;

    return (
      <div className="min-h-screen bg-[#f8fafc]">
        <Sidebar collapsed={collapsed} setCollapsed={setCollapsed} mobileOpen={mobileOpen} setMobileOpen={setMobileOpen} />
        <Header collapsed={collapsed} setMobileOpen={setMobileOpen} />
        <main className={cn("transition-all duration-300 p-6 md:p-8 pt-24", collapsed ? "md:ml-20" : "md:ml-64")}>
          {/* Status Header */}
          <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-[#0f172a] via-[#1e293b] to-[#0f172a] p-8 mb-8 mt-4">
            <div className="absolute top-0 right-0 w-96 h-96 bg-orange-500/10 rounded-full blur-3xl" />
            <div className="relative z-10">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-orange-500 to-red-500 flex items-center justify-center">
                  <Rocket className="w-6 h-6 text-white" />
                </div>
                <Badge className={cn("border px-3 py-1", statusInfo.color)}>
                  <StatusIcon className="w-4 h-4 mr-1" />
                  {statusInfo.label}
                </Badge>
              </div>
              <h1 className="text-2xl md:text-3xl font-bold text-white mb-2">
                Votre demande <span className="text-orange-400">Copilote</span>
              </h1>
              <p className="text-slate-400">
                Soumise le {formatDate(existingRequest.created_at)}
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Main Status Card */}
            <Card className="card lg:col-span-2">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <StatusIcon className="w-5 h-5" style={{ color: existingRequest.status === 'approved' || existingRequest.status === 'rdv_booked' ? '#10b981' : '#eab308' }} />
                  Statut de votre demande
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Pending Status */}
                {existingRequest.status === 'pending' && (
                  <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-6">
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 rounded-full bg-yellow-100 flex items-center justify-center shrink-0">
                        <Clock className="w-6 h-6 text-yellow-600" />
                      </div>
                      <div>
                        <h3 className="font-bold text-yellow-800 mb-2">Demande en cours d'analyse</h3>
                        <p className="text-yellow-700 text-sm">
                          Notre équipe étudie votre projet. Vous recevrez une réponse sous 48h ouvrées.
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Approved Status with RDV Selection */}
                {existingRequest.status === 'approved' && (
                  <div className="space-y-6">
                    <div className="bg-green-50 border border-green-200 rounded-xl p-6">
                      <div className="flex items-start gap-4">
                        <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center shrink-0">
                          <PartyPopper className="w-6 h-6 text-green-600" />
                        </div>
                        <div>
                          <h3 className="font-bold text-green-800 mb-2">Félicitations ! Votre demande est approuvée</h3>
                          <p className="text-green-700 text-sm">
                            {existingRequest.review_notes || "Nous sommes ravis de vous accueillir dans le programme Copilote. Sélectionnez un créneau pour votre rendez-vous de lancement."}
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Account Manager Info */}
                    {existingRequest.account_manager_name && (
                      <div className="bg-[#f8fafc] rounded-xl p-4 flex items-center gap-4">
                        <div className="w-12 h-12 rounded-full bg-gradient-to-br from-orange-500 to-red-500 flex items-center justify-center">
                          <User className="w-6 h-6 text-white" />
                        </div>
                        <div>
                          <p className="text-sm text-[#64748b]">Votre responsable de compte</p>
                          <p className="font-bold text-[#1a365d]">{existingRequest.account_manager_name}</p>
                        </div>
                      </div>
                    )}

                    {/* RDV Slot Selection */}
                    {existingRequest.proposed_rdv_slots && existingRequest.proposed_rdv_slots.length > 0 && (
                      <div>
                        <h4 className="font-medium text-[#1a365d] mb-3 flex items-center gap-2">
                          <CalendarDays className="w-5 h-5 text-orange-500" />
                          Sélectionnez un créneau pour votre RDV de lancement
                        </h4>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                          {existingRequest.proposed_rdv_slots.map((slot, index) => (
                            <button
                              key={index}
                              onClick={() => setSelectedRdvSlot(slot)}
                              className={cn(
                                "p-4 rounded-xl border-2 text-left transition-all",
                                selectedRdvSlot === slot
                                  ? "border-orange-500 bg-orange-50"
                                  : "border-[#e2e8f0] hover:border-orange-300"
                              )}
                            >
                              <p className="font-medium text-[#1a365d]">{formatDate(slot)}</p>
                            </button>
                          ))}
                        </div>
                        <Button
                          onClick={handleBookRdv}
                          disabled={!selectedRdvSlot || bookingRdv}
                          className="w-full mt-4 bg-gradient-to-r from-orange-500 to-red-500 text-white rounded-full py-3"
                        >
                          {bookingRdv ? (
                            <Loader2 className="w-4 h-4 animate-spin mr-2" />
                          ) : (
                            <CalendarDays className="w-4 h-4 mr-2" />
                          )}
                          Confirmer le RDV
                        </Button>
                      </div>
                    )}
                  </div>
                )}

                {/* RDV Booked Status */}
                {existingRequest.status === 'rdv_booked' && (
                  <div className="space-y-6">
                    <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-6">
                      <div className="flex items-start gap-4">
                        <div className="w-12 h-12 rounded-full bg-emerald-100 flex items-center justify-center shrink-0">
                          <CalendarDays className="w-6 h-6 text-emerald-600" />
                        </div>
                        <div>
                          <h3 className="font-bold text-emerald-800 mb-2">RDV confirmé !</h3>
                          <p className="text-emerald-700 text-sm mb-2">
                            Votre rendez-vous de lancement Copilote est prévu pour :
                          </p>
                          <p className="text-lg font-bold text-emerald-800">
                            {formatDate(existingRequest.selected_rdv_slot)}
                          </p>
                        </div>
                      </div>
                    </div>

                    {existingRequest.account_manager_name && (
                      <div className="bg-[#f8fafc] rounded-xl p-4 flex items-center gap-4">
                        <div className="w-12 h-12 rounded-full bg-gradient-to-br from-orange-500 to-red-500 flex items-center justify-center">
                          <User className="w-6 h-6 text-white" />
                        </div>
                        <div>
                          <p className="text-sm text-[#64748b]">Votre responsable de compte</p>
                          <p className="font-bold text-[#1a365d]">{existingRequest.account_manager_name}</p>
                        </div>
                      </div>
                    )}

                    <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                      <p className="text-sm text-blue-700">
                        <strong>Conseil :</strong> Préparez vos questions et objectifs pour ce premier échange. 
                        Nous analyserons ensemble les meilleures opportunités de croissance.
                      </p>
                    </div>
                  </div>
                )}

                {/* Rejected Status */}
                {existingRequest.status === 'rejected' && (
                  <div className="bg-red-50 border border-red-200 rounded-xl p-6">
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center shrink-0">
                        <XCircle className="w-6 h-6 text-red-600" />
                      </div>
                      <div>
                        <h3 className="font-bold text-red-800 mb-2">Demande non retenue</h3>
                        <p className="text-red-700 text-sm">
                          {existingRequest.review_notes || "Votre profil ne correspond pas aux critères du programme Copilote pour le moment. N'hésitez pas à nous recontacter si votre situation évolue."}
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Summary Card */}
            <Card className="card">
              <CardHeader>
                <CardTitle className="text-lg">Récapitulatif</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-3 text-sm">
                  <div className="flex justify-between">
                    <span className="text-[#64748b]">Secteur</span>
                    <span className="font-medium text-[#1a365d]">{existingRequest.sector || '-'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-[#64748b]">Modèle</span>
                    <span className="font-medium text-[#1a365d]">{existingRequest.client_type || '-'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-[#64748b]">Budget</span>
                    <span className="font-medium text-[#1a365d]">{existingRequest.budget || 0}€/mois</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-[#64748b]">Canaux actifs</span>
                    <span className="font-medium text-[#1a365d]">{existingRequest.current_channels?.length || 0}</span>
                  </div>
                </div>
                
                {existingRequest.objectives && existingRequest.objectives.length > 0 && (
                  <div className="pt-4 border-t border-[#e2e8f0]">
                    <p className="text-sm text-[#64748b] mb-2">Objectifs prioritaires</p>
                    <div className="flex flex-wrap gap-2">
                      {existingRequest.objectives.slice(0, 3).map((obj) => (
                        <Badge key={obj} variant="secondary" className="text-xs">
                          {objectivesList.find(o => o.id === obj)?.label || obj}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#f8fafc]">
      <Sidebar collapsed={collapsed} setCollapsed={setCollapsed} mobileOpen={mobileOpen} setMobileOpen={setMobileOpen} />
      <Header collapsed={collapsed} setMobileOpen={setMobileOpen} />
      
      <main className={cn("transition-all duration-300 p-6 md:p-8 pt-24", collapsed ? "md:ml-20" : "md:ml-64")}>
        {/* Hero */}
        <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 p-8 mb-8">
          <div className="absolute top-0 right-0 w-96 h-96 bg-orange-500/10 rounded-full blur-3xl" />
          <div className="absolute bottom-0 left-0 w-64 h-64 bg-red-500/10 rounded-full blur-3xl" />
          <div className="relative z-10 flex flex-col md:flex-row md:items-center md:justify-between gap-6">
            <div className="max-w-xl">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-orange-500 to-red-500 flex items-center justify-center relative">
                  <Rocket className="w-6 h-6 text-white" />
                  {/* Flame effect */}
                  <div className="absolute -bottom-1 left-1/2 -translate-x-1/2 w-3 h-4 bg-gradient-to-t from-yellow-400 via-orange-500 to-transparent rounded-full opacity-80 animate-pulse" />
                </div>
                <Badge className="bg-orange-500/20 text-orange-400 border border-orange-500/30">MISSION POSSIBLE</Badge>
              </div>
              <h1 className="text-2xl md:text-3xl font-bold text-white mb-2">
                Demande d'éligibilité <span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-400 to-red-400">COPILOTE</span>
              </h1>
              <p className="text-slate-400">
                Complétez ce formulaire pour que nous puissions évaluer votre éligibilité 
                au programme d'accompagnement stratégique sur 12 mois.
              </p>
            </div>
            <div className="hidden md:grid grid-cols-3 gap-3">
              {benefits.slice(0, 3).map((b, i) => (
                <div key={i} className="text-center p-3 rounded-xl bg-white/5 border border-white/10">
                  <b.icon className="w-5 h-5 text-orange-400 mx-auto mb-1" />
                  <p className="text-xs text-white font-medium">{b.title}</p>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Steps indicator */}
        <div className="flex items-center justify-center gap-2 mb-8">
          {steps.map((step, idx) => {
            const Icon = step.icon;
            return (
              <React.Fragment key={idx}>
                <button
                  onClick={() => idx < currentStep && setCurrentStep(idx)}
                  className={cn(
                    "flex items-center gap-2 px-4 py-2 rounded-full transition-all",
                    idx === currentStep
                      ? "bg-gradient-to-r from-orange-500 to-red-500 text-white"
                      : idx < currentStep
                        ? "bg-emerald-100 text-emerald-700 cursor-pointer hover:bg-emerald-200"
                        : "bg-gray-100 text-gray-400"
                  )}
                >
                  {idx < currentStep ? (
                    <CheckCircle className="w-4 h-4" />
                  ) : (
                    <Icon className="w-4 h-4" />
                  )}
                  <span className="text-sm font-medium hidden sm:inline">{step.title}</span>
                  <span className="text-sm font-medium sm:hidden">{idx + 1}</span>
                </button>
                {idx < steps.length - 1 && (
                  <ChevronRight className="w-4 h-4 text-gray-300" />
                )}
              </React.Fragment>
            );
          })}
        </div>

        {/* Form Steps */}
        <Card className="card max-w-3xl mx-auto">
          <CardContent className="p-6 md:p-8">
            {/* Step 1: Company Info */}
            {currentStep === 0 && (
              <div className="space-y-6">
                <div>
                  <h3 className="text-lg font-semibold text-[#1a365d] mb-4 flex items-center gap-2">
                    <Building2 className="w-5 h-5 text-orange-500" />
                    Parlez-nous de votre entreprise
                  </h3>
                </div>

                <div className="space-y-2">
                  <Label>Secteur d'activité *</Label>
                  <Select value={formData.sector} onValueChange={(v) => setFormData({...formData, sector: v})}>
                    <SelectTrigger>
                      <SelectValue placeholder="Sélectionnez votre secteur" />
                    </SelectTrigger>
                    <SelectContent>
                      {sectors.map((s) => (
                        <SelectItem key={s} value={s}>{s}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Panier moyen de vos clients</Label>
                  <Input
                    value={formData.avgBasket}
                    onChange={(e) => setFormData({...formData, avgBasket: e.target.value})}
                    placeholder="Ex: 150€, 500-1000€, > 5000€..."
                  />
                </div>

                <div className="space-y-2">
                  <Label>Votre cible principale *</Label>
                  <div className="grid grid-cols-2 gap-3">
                    {[
                      { id: 'B2C', label: 'B2C', desc: 'Particuliers' },
                      { id: 'B2B', label: 'B2B', desc: 'Entreprises' },
                      { id: 'both', label: 'Les deux', desc: 'B2B et B2C' },
                      { id: 'BtoBtoC', label: 'BtoBtoC', desc: 'Distributeurs' }
                    ].map((opt) => (
                      <button
                        key={opt.id}
                        onClick={() => setFormData({...formData, clientType: opt.id})}
                        className={cn(
                          "p-4 rounded-xl border-2 text-left transition-all",
                          formData.clientType === opt.id
                            ? "border-orange-500 bg-orange-50"
                            : "border-gray-200 hover:border-gray-300"
                        )}
                      >
                        <p className="font-medium text-[#1a365d]">{opt.label}</p>
                        <p className="text-xs text-[#64748b]">{opt.desc}</p>
                      </button>
                    ))}
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Disposez-vous d'un fichier client ? *</Label>
                  <div className="flex gap-3">
                    <Button
                      variant={formData.hasClientFile === true ? "default" : "outline"}
                      onClick={() => setFormData({...formData, hasClientFile: true})}
                      className={formData.hasClientFile === true ? "bg-orange-500" : ""}
                    >
                      Oui
                    </Button>
                    <Button
                      variant={formData.hasClientFile === false ? "default" : "outline"}
                      onClick={() => setFormData({...formData, hasClientFile: false})}
                      className={formData.hasClientFile === false ? "bg-gray-500" : ""}
                    >
                      Non
                    </Button>
                  </div>
                </div>
              </div>
            )}

            {/* Step 2: Current Communication */}
            {currentStep === 1 && (
              <div className="space-y-6">
                <div>
                  <h3 className="text-lg font-semibold text-[#1a365d] mb-4 flex items-center gap-2">
                    <Megaphone className="w-5 h-5 text-orange-500" />
                    Votre communication actuelle
                  </h3>
                </div>

                <div className="space-y-2">
                  <Label>Quels canaux de communication utilisez-vous actuellement ? *</Label>
                  <p className="text-xs text-[#64748b] mb-3">Sélectionnez tous les canaux actifs</p>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-2 max-h-[300px] overflow-y-auto p-1">
                    {communicationChannels.map((channel) => {
                      const Icon = channel.icon;
                      return (
                        <button
                          key={channel.id}
                          onClick={() => toggleChannel(channel.id)}
                          className={cn(
                            "flex items-center gap-2 p-3 rounded-lg border text-sm text-left transition-all",
                            formData.currentChannels.includes(channel.id)
                              ? "bg-orange-50 border-orange-300 text-orange-700"
                              : "border-gray-200 hover:border-gray-300"
                          )}
                        >
                          {formData.currentChannels.includes(channel.id) && (
                            <CheckCircle className="w-4 h-4 flex-shrink-0" />
                          )}
                          {!formData.currentChannels.includes(channel.id) && (
                            <Icon className="w-4 h-4 flex-shrink-0 text-gray-400" />
                          )}
                          <span className="truncate">{channel.label}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Qui gère actuellement votre communication ? *</Label>
                  <Select 
                    value={formData.whoManagesCommunication} 
                    onValueChange={(v) => setFormData({...formData, whoManagesCommunication: v})}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Sélectionnez une option" />
                    </SelectTrigger>
                    <SelectContent>
                      {whoManagesOptions.map((opt) => (
                        <SelectItem key={opt} value={opt}>{opt}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Avez-vous une force commerciale ? *</Label>
                  <div className="flex gap-3">
                    <Button
                      variant={formData.hasCommercialTeam === true ? "default" : "outline"}
                      onClick={() => setFormData({...formData, hasCommercialTeam: true})}
                      className={formData.hasCommercialTeam === true ? "bg-orange-500" : ""}
                    >
                      Oui
                    </Button>
                    <Button
                      variant={formData.hasCommercialTeam === false ? "default" : "outline"}
                      onClick={() => setFormData({...formData, hasCommercialTeam: false})}
                      className={formData.hasCommercialTeam === false ? "bg-gray-500" : ""}
                    >
                      Non
                    </Button>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Avez-vous 1h/semaine de disponible pour suivre la mission ? *</Label>
                  <p className="text-xs text-[#64748b] mb-2">Points réguliers, validation, feedback...</p>
                  <div className="flex gap-3">
                    <Button
                      variant={formData.availableTime === true ? "default" : "outline"}
                      onClick={() => setFormData({...formData, availableTime: true})}
                      className={formData.availableTime === true ? "bg-emerald-500" : ""}
                    >
                      <CheckCircle className="w-4 h-4 mr-2" />
                      Oui, je suis disponible
                    </Button>
                    <Button
                      variant={formData.availableTime === false ? "default" : "outline"}
                      onClick={() => setFormData({...formData, availableTime: false})}
                      className={formData.availableTime === false ? "bg-amber-500" : ""}
                    >
                      Difficile
                    </Button>
                  </div>
                </div>
              </div>
            )}

            {/* Step 3: Budget & Objectives */}
            {currentStep === 2 && (
              <div className="space-y-6">
                <div>
                  <h3 className="text-lg font-semibold text-[#1a365d] mb-4 flex items-center gap-2">
                    <Target className="w-5 h-5 text-orange-500" />
                    Budget & Objectifs
                  </h3>
                </div>

                <div className="space-y-4">
                  <Label>Budget mensuel envisagé *</Label>
                  <div className="bg-[#f8fafc] rounded-xl p-6">
                    <div className="flex items-center justify-between mb-4">
                      <span className="text-sm text-[#64748b]">Minimum: 1 000€</span>
                      <span className="text-2xl font-bold text-[#1a365d]">{formData.budget.toLocaleString('fr-FR')}€</span>
                      <span className="text-sm text-[#64748b]">Max: 10 000€+</span>
                    </div>
                    <Slider
                      value={[formData.budget]}
                      onValueChange={(v) => setFormData({...formData, budget: v[0]})}
                      min={1000}
                      max={10000}
                      step={500}
                      className="mb-2"
                    />
                    <p className="text-xs text-center text-[#64748b]">
                      Déplacez le curseur pour ajuster votre budget
                    </p>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Quels sont vos objectifs principaux ? *</Label>
                  <p className="text-xs text-[#64748b] mb-3">
                    Sélectionnez vos objectifs et attribuez-leur un numéro de priorité
                  </p>
                  <div className="space-y-2">
                    {objectivesList.map((obj) => {
                      const Icon = obj.icon;
                      const isSelected = formData.objectives.includes(obj.id);
                      const priority = formData.objectivesPriority[obj.id];
                      
                      return (
                        <div
                          key={obj.id}
                          className={cn(
                            "flex items-center gap-3 p-3 rounded-xl border-2 transition-all",
                            isSelected
                              ? "border-orange-500 bg-orange-50"
                              : "border-gray-200 hover:border-gray-300"
                          )}
                        >
                          <button
                            onClick={() => toggleObjective(obj.id)}
                            className="flex items-center gap-3 flex-1"
                          >
                            <div className={cn(
                              "w-8 h-8 rounded-lg flex items-center justify-center",
                              isSelected ? "bg-orange-500 text-white" : "bg-gray-100 text-gray-400"
                            )}>
                              {isSelected ? <CheckCircle className="w-4 h-4" /> : <Icon className="w-4 h-4" />}
                            </div>
                            <span className={cn(
                              "text-sm font-medium",
                              isSelected ? "text-orange-700" : "text-gray-600"
                            )}>
                              {obj.label}
                            </span>
                          </button>
                          
                          {isSelected && (
                            <div className="flex items-center gap-2">
                              <span className="text-xs text-[#64748b]">Priorité:</span>
                              <Select
                                value={String(priority || '')}
                                onValueChange={(v) => updatePriority(obj.id, parseInt(v))}
                              >
                                <SelectTrigger className="w-16 h-8">
                                  <SelectValue placeholder="#" />
                                </SelectTrigger>
                                <SelectContent>
                                  {[1, 2, 3, 4, 5].map((n) => (
                                    <SelectItem key={n} value={String(n)}>#{n}</SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Décrivez vos attentes et le contexte de votre demande *</Label>
                  <Textarea
                    value={formData.expectations}
                    onChange={(e) => setFormData({...formData, expectations: e.target.value})}
                    placeholder="Quels résultats attendez-vous du programme Copilote ? Y a-t-il des contraintes particulières ? Des projets en cours ?..."
                    className="min-h-[120px]"
                  />
                </div>
              </div>
            )}

            {/* Navigation */}
            <div className="flex items-center justify-between mt-8 pt-6 border-t">
              <Button
                variant="outline"
                onClick={() => setCurrentStep(Math.max(0, currentStep - 1))}
                disabled={currentStep === 0}
              >
                <ChevronLeft className="w-4 h-4 mr-2" />
                Précédent
              </Button>
              
              {currentStep < steps.length - 1 ? (
                <Button
                  onClick={() => setCurrentStep(currentStep + 1)}
                  disabled={!canProceed()}
                  className="bg-[#1a365d]"
                >
                  Suivant
                  <ChevronRight className="w-4 h-4 ml-2" />
                </Button>
              ) : (
                <Button
                  onClick={handleSubmit}
                  disabled={!canProceed() || submitting}
                  className="bg-gradient-to-r from-orange-500 to-red-500"
                >
                  {submitting ? (
                    'Envoi en cours...'
                  ) : (
                    <>
                      <Send className="w-4 h-4 mr-2" />
                      Envoyer ma demande
                    </>
                  )}
                </Button>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Eligibility Notice */}
        <Card className="card max-w-3xl mx-auto mt-6 border-amber-200 bg-amber-50/50">
          <CardContent className="p-4 flex items-start gap-3">
            <Award className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <div>
              <h4 className="font-medium text-amber-800 text-sm">Éligibilité soumise à accord</h4>
              <p className="text-xs text-amber-700">
                Le programme Copilote est un accompagnement premium. Notre équipe évaluera votre demande 
                pour s'assurer que nous pouvons générer un ROI significatif pour votre activité.
              </p>
            </div>
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default CopiloteDiscoverPage;
